import { Controller } from "@hotwired/stimulus"

// Breakfast DNA Quiz (Phase 1 item 1.5).
//
// Flow:
//   1. Pick an answer for question N → question card slides to N+1
//   2. After the last question, the email gate appears
//   3. POST { answers, email } to /quiz/breakfast-dna; server scores,
//      persists, returns the share URL
//   4. Browser redirects to the result page
//
// State lives entirely on this controller (a Map of questionKey →
// choiceKey). No per-question network round-trip — keeps the quiz fast
// and the scoring deterministic against QuizScorer's pure-ruby weights.
export default class extends Controller {
  static targets = [
    "question", "progress", "progressBar",
    "emailGate", "emailInput", "consentInput", "submitButton", "errorBanner"
  ]

  static values = {
    submitUrl: String,
    questionCount: Number
  }

  connect() {
    this._answers = new Map()
    this._currentIndex = 0
    this._showQuestion(0)
    this._updateProgress()
  }

  // Bound to each answer button. Records the answer + advances.
  selectAnswer(event) {
    const btn = event.currentTarget
    const questionKey = btn.dataset.question
    const choiceKey = btn.dataset.choice
    if (!questionKey || !choiceKey) return

    this._answers.set(questionKey, choiceKey)
    this._highlightSelected(questionKey, choiceKey)

    const nextIndex = this._currentIndex + 1
    if (nextIndex >= this.questionCountValue) {
      this._showEmailGate()
      if (this._canAutoSubmit()) {
        this._setSubmitting(true)
        this._performSubmit()
      }
    } else {
      this._currentIndex = nextIndex
      this._showQuestion(nextIndex)
      this._updateProgress()
    }
  }

  // Used by "Back" buttons within each question card.
  goBack() {
    if (this._currentIndex === 0) return
    if (this.hasEmailGateTarget) this.emailGateTarget.classList.add("hidden")
    this._currentIndex -= 1
    this._showQuestion(this._currentIndex)
    this._updateProgress()
  }

  // Submits the completed quiz. Triggered by the email-gate form.
  async submit(event) {
    event.preventDefault()
    this._setSubmitting(true)
    await this._performSubmit()
  }

  async _performSubmit() {
    this._clearError()

    if (this._answers.size < this.questionCountValue) {
      this._showError("Please answer every question first.")
      this._setSubmitting(false)
      return
    }

    const email = this.hasEmailInputTarget ? this.emailInputTarget.value.trim() : ""
    if (!this._isValidEmail(email)) {
      this._showError("Please enter a valid email address.")
      this._setSubmitting(false)
      return
    }

    const consent = this.hasConsentInputTarget ? this.consentInputTarget.checked : false

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          answers: this._answersArray(),
          email: email,
          marketing_consent: consent
        })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.status === "saved" && data.result_url) {
        window.location.href = data.result_url
      } else {
        this._showError(data.message || "Something went wrong — please try again.")
        this._setSubmitting(false)
      }
    } catch (err) {
      this._showError("Network error — please try again.")
      this._setSubmitting(false)
    }
  }

  _canAutoSubmit() {
    if (!this.hasEmailInputTarget) return false
    return this._isValidEmail(this.emailInputTarget.value.trim())
  }

  // ---- internals ----

  _showQuestion(idx) {
    this.questionTargets.forEach((el, i) => {
      el.classList.toggle("hidden", i !== idx)
    })
    if (this.hasEmailGateTarget) this.emailGateTarget.classList.add("hidden")
  }

  _showEmailGate() {
    this.questionTargets.forEach((el) => el.classList.add("hidden"))
    if (this.hasEmailGateTarget) {
      this.emailGateTarget.classList.remove("hidden")
      if (this.hasEmailInputTarget) this.emailInputTarget.focus()
    }
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "100%"
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `${this.questionCountValue} of ${this.questionCountValue}`
    }
  }

  _highlightSelected(questionKey, choiceKey) {
    const card = this.questionTargets.find((el) => el.dataset.question === questionKey)
    if (!card) return
    card.querySelectorAll("[data-choice]").forEach((btn) => {
      btn.dataset.selected = (btn.dataset.choice === choiceKey).toString()
    })
  }

  _updateProgress() {
    const current = this._currentIndex + 1
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `${current} of ${this.questionCountValue}`
    }
    if (this.hasProgressBarTarget) {
      const pct = Math.round((this._currentIndex / this.questionCountValue) * 100)
      this.progressBarTarget.style.width = `${pct}%`
    }
  }

  _answersArray() {
    return Array.from(this._answers.entries()).map(([question, choice]) => ({ question, choice }))
  }

  _isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  _showError(message) {
    if (!this.hasErrorBannerTarget) return
    this.errorBannerTarget.textContent = message
    this.errorBannerTarget.classList.remove("hidden")
  }

  _clearError() {
    if (!this.hasErrorBannerTarget) return
    this.errorBannerTarget.textContent = ""
    this.errorBannerTarget.classList.add("hidden")
  }

  _setSubmitting(submitting) {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = submitting
    this.submitButtonTarget.textContent = submitting ? "Finding your persona…" : "Reveal my persona"
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this._csrfToken()
    }
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
